import { useEffect, useState } from 'react';
import api from '../api';

const SECTIONS = [
  { key: 'ORDERS', label: 'Logi zamówień' },
  { key: 'TASKS', label: 'Logi do zrobienia' },
  { key: 'CALENDAR', label: 'Logi kalendarza' },
  { key: 'AUTH', label: 'Logowania' },
];

function formatDateTime(value) {
  if (!value) return '-';
  return new Date(value).toLocaleString('pl-PL', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function Logs() {
  const [section, setSection] = useState('ORDERS');
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const limit = 100;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [retentionDays, setRetentionDays] = useState(180);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/logs', { params: { section, page, limit } });
      setLogs(res.data.logs || []);
      setTotal(res.data.total || 0);
      setRetentionDays(res.data.settings?.retention_days || 180);
    } catch (err) {
      setError(err.response?.data?.error || 'Nie udało się pobrać logów');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [section, page]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveSettings = async () => {
    setSaving(true);
    setSaved(false);
    setError('');
    try {
      const days = Math.max(1, Number(retentionDays) || 1);
      const res = await api.put('/logs/settings', { retention_days: days });
      setRetentionDays(res.data.retention_days);
      setSaved(true);
      await load();
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setError(err.response?.data?.error || 'Nie udało się zapisać ustawień');
    } finally {
      setSaving(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));
  const from = total === 0 ? 0 : (page - 1) * limit + 1;
  const to = Math.min(page * limit, total);

  return (
    <div style={s.page}>
      <div style={s.header}>
        <div>
          <h2 style={s.title}>Logi</h2>
          <p style={s.subtitle}>Historia zmian wykonanych przez użytkowników</p>
        </div>
      </div>

      <div style={s.toolbar}>
        <div style={s.tabs}>
          {SECTIONS.map(opt => (
            <button
              key={opt.key}
              style={section === opt.key ? s.tabActive : s.tab}
              onClick={() => {
                setSection(opt.key);
                setPage(1);
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>

        <div style={s.settings}>
          <span style={s.settingsLabel}>Przechowuj logi przez</span>
          <input
            style={s.daysInput}
            type="number"
            min="1"
            value={retentionDays}
            onChange={e => setRetentionDays(e.target.value)}
          />
          <span style={s.settingsLabel}>dni</span>
          <button style={s.saveBtn} onClick={saveSettings} disabled={saving}>
            {saving ? 'Zapisywanie...' : saved ? 'Zapisano' : 'Zapisz'}
          </button>
        </div>
      </div>

      {error && <div style={s.error}>{error}</div>}

      <div style={s.tableWrap}>
        <div style={s.tableHeader}>
          <span>Ostatnie wpisy</span>
          <span style={s.count}>{total > 0 ? `${from}-${to} z ${total}` : 0}</span>
        </div>
        {loading ? (
          <div style={s.empty}>Ładowanie...</div>
        ) : logs.length === 0 ? (
          <div style={s.empty}>Brak logów w tej grupie</div>
        ) : (
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Data i godzina</th>
                <th style={s.th}>Użytkownik</th>
                <th style={s.th}>Czynność</th>
                <th style={s.th}>Obiekt</th>
              </tr>
            </thead>
            <tbody>
              {logs.map(log => (
                <tr key={log.id}>
                  <td style={s.td}>{formatDateTime(log.created_at)}</td>
                  <td style={s.td}>{log.user_name || '-'}</td>
                  <td style={s.td}>{log.action}</td>
                  <td style={s.td}>
                    {[log.entity_label, log.entity_id ? `#${log.entity_id}` : null].filter(Boolean).join(' ') || '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {totalPages > 1 && (
          <div style={s.pagination}>
            <button
              style={page <= 1 ? s.pageBtnDisabled : s.pageBtn}
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1 || loading}
            >
              Poprzednia
            </button>
            <span style={s.pageInfo}>Strona {page} z {totalPages}</span>
            <button
              style={page >= totalPages ? s.pageBtnDisabled : s.pageBtn}
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages || loading}
            >
              Następna
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const s = {
  page: { padding: 24, color: '#c9d1d9' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 },
  title: { margin: 0, fontSize: 24, color: '#e6edf3' },
  subtitle: { margin: '4px 0 0', color: '#8b949e', fontSize: 13 },
  toolbar: { display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 16 },
  tabs: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  tab: { background: '#21262d', border: '1px solid #30363d', color: '#8b949e', borderRadius: 4, padding: '7px 12px', cursor: 'pointer', fontSize: 13 },
  tabActive: { background: '#1c3a5e', border: '1px solid #1f4068', color: '#58a6ff', borderRadius: 4, padding: '7px 12px', cursor: 'pointer', fontSize: 13 },
  settings: { display: 'flex', alignItems: 'center', gap: 8, background: '#161b22', border: '1px solid #30363d', borderRadius: 6, padding: '8px 10px' },
  settingsLabel: { color: '#8b949e', fontSize: 13 },
  daysInput: { width: 76, background: '#0d1117', border: '1px solid #30363d', borderRadius: 4, color: '#c9d1d9', padding: '5px 7px', fontSize: 13 },
  saveBtn: { background: '#238636', border: 'none', color: 'white', borderRadius: 4, padding: '6px 10px', cursor: 'pointer', fontSize: 13 },
  error: { background: '#3a1518', border: '1px solid #7d252b', color: '#ffb3b8', padding: 10, borderRadius: 6, marginBottom: 12 },
  tableWrap: { background: '#161b22', border: '1px solid #30363d', borderRadius: 8, overflow: 'hidden' },
  tableHeader: { display: 'flex', justifyContent: 'space-between', padding: '10px 12px', background: '#1c2128', borderBottom: '1px solid #30363d', fontWeight: 700 },
  count: { color: '#8b949e', fontWeight: 500 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { padding: '9px 12px', textAlign: 'left', color: '#8b949e', borderBottom: '1px solid #30363d', fontWeight: 600 },
  td: { padding: '9px 12px', borderBottom: '1px solid #21262d', verticalAlign: 'top' },
  pagination: { display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10, padding: '10px 12px', borderTop: '1px solid #30363d', background: '#1c2128' },
  pageBtn: { background: '#21262d', border: '1px solid #30363d', color: '#c9d1d9', borderRadius: 4, padding: '6px 10px', cursor: 'pointer', fontSize: 13 },
  pageBtnDisabled: { background: '#161b22', border: '1px solid #30363d', color: '#484f58', borderRadius: 4, padding: '6px 10px', cursor: 'not-allowed', fontSize: 13 },
  pageInfo: { color: '#8b949e', fontSize: 13 },
  empty: { padding: 24, color: '#8b949e', textAlign: 'center' },
};
